import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { formatDate, formatTime } from 'utils/date'
import ProvenanceChip from 'features/bpDetails/Components/files/ProvenanceChip'
import ReviewActionSlider from 'features/bpDetails/Components/files/ReviewActionSlider'
import ReviewActionsLegend from 'features/bpDetails/Components/files/ReviewActionsLegend'
import useFilePreview from 'features/bpDetails/Components/files/useFilePreview'
import useRowSelection from 'features/bpDetails/Components/files/useRowSelection'
import BulkActionBar, { BulkBtn } from 'features/bpDetails/Components/files/BulkActionBar'
import FilterSelect from 'features/bpDetails/Components/files/FilterSelect'
import { getReviewFiles, setFileReviewAction, getFileReviewActions, markFilesForDeletion } from 'services/fileService'

const PAGE = 25

const SOURCES = [
  { key: 'sharepoint', label: 'SharePoint' },
  { key: 'email', label: 'Email' },
  { key: 'upload', label: 'Uploads' },
  { key: 'all', label: 'All' }
]

const fmt = n => (n ?? 0).toLocaleString('en-GB')

const ReviewPanel = ({ bp, canEdit, onOpenInsight }) => {
  const bpId = bp?.id
  const { open: openPreview, previewingId, modal: previewModal } = useFilePreview(bpId)
  const selection = useRowSelection()
  const { clear: resetSelection } = selection
  const [bulkBusy, setBulkBusy] = useState(false)
  const [view, setView] = useState('review')

  const [files, setFiles] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [query, setQuery] = useState('')
  const [source, setSource] = useState('sharepoint')
  const [fileType, setFileType] = useState(null)
  const [fileCategory, setFileCategory] = useState(null)
  const [filters, setFilters] = useState({ types: [], categories: [] })
  const [busyId, setBusyId] = useState(null)
  const [flaggedOnly, setFlaggedOnly] = useState(false)

  const [actioned, setActioned] = useState([])
  const [actionedMeta, setActionedMeta] = useState(null)
  const [actionedLoading, setActionedLoading] = useState(true)
  const [actionedLoadingMore, setActionedLoadingMore] = useState(false)
  const [sortDir, setSortDir] = useState('desc')

  const load = useCallback((page, search, src, typeId, catId, flg) => {
    if (!bpId) return
    if (page === 1) setLoading(true)
    else setLoadingMore(true)
    getReviewFiles(bpId, { page, perPage: PAGE, search, source: src, fileTypeId: typeId, fileCategoryId: catId, flagged: flg })
      .then(res => {
        const incoming = res?.data?.data || []
        const m = res?.data?.meta || null
        setMeta(m)
        if (m?.filters) setFilters(m.filters)
        setFiles(prev =>
          (m?.currentPage ?? 1) > 1
            ? [...prev, ...incoming.filter(i => !prev.some(x => x.id === i.id))]
            : incoming
        )
      })
      .catch(() => toast.error('Could not load files to review. Please try again.'))
      .finally(() => { setLoading(false); setLoadingMore(false) })
  }, [bpId])

  const loadActioned = useCallback((page, direction) => {
    if (!bpId) return
    if (page === 1) setActionedLoading(true)
    else setActionedLoadingMore(true)
    getFileReviewActions(bpId, { page, perPage: PAGE, direction })
      .then(res => {
        const incoming = res?.data?.data?.actions || []
        const m = res?.data?.meta || null
        setActionedMeta(m)
        setActioned(prev =>
          (m?.currentPage ?? 1) > 1
            ? [...prev, ...incoming.filter(i => !prev.some(x => x.id === i.id))]
            : incoming
        )
      })
      .catch(() => {})
      .finally(() => { setActionedLoading(false); setActionedLoadingMore(false) })
  }, [bpId])

  useEffect(() => { loadActioned(1, sortDir) }, [sortDir, loadActioned])

  useEffect(() => {
    const t = setTimeout(() => load(1, query, source, fileType, fileCategory, flaggedOnly), query ? 300 : 0)
    return () => clearTimeout(t)
  }, [query, source, fileType, fileCategory, flaggedOnly, load])

  const typeOptions = useMemo(
    () => (fileCategory ? filters.types.filter(t => String(t.categoryId) === String(fileCategory)) : filters.types),
    [filters.types, fileCategory]
  )

  const onSelectCategory = catId => {
    setFileCategory(catId)
    if (catId && fileType) {
      const stillValid = filters.types.some(t => String(t.id) === String(fileType) && String(t.categoryId) === String(catId))
      if (!stillValid) setFileType(null)
    }
  }

  const clearTypeFilters = () => { setFileType(null); setFileCategory(null) }

  const shortId = id => String(id || '').slice(0, 8)
  const copyRef = id => {
    navigator.clipboard?.writeText(String(id)).then(
      () => toast.success('File reference copied.'),
      () => toast.error('Could not copy the reference.')
    )
  }

  const apply = (file, action) => {
    if (busyId) return
    const isSharepoint = file.provenance === 'sharepoint'
    if (action === 'delete' && !window.confirm(
      isSharepoint
        ? `Mark “${file.displayName}” for deletion? SharePoint files can’t be deleted here, so this adds it to the SharePoint cleanse list to remove at the source.`
        : `Remove “${file.displayName}” from this Building Passport? It moves to the Actioned tab.`
    )) return
    setBusyId(file.id)
    const primary = (action === 'delete' && isSharepoint)
      ? markFilesForDeletion(bpId, [file.id])
      : setFileReviewAction(bpId, file.id, action)
    primary
      .then(() => {
        setFiles(prev => prev.filter(f => f.id !== file.id))
        setMeta(m => (m ? { ...m, total: Math.max(0, (m.total || 1) - 1) } : m))
        if (!(action === 'delete' && isSharepoint)) loadActioned(1, sortDir)
        toast.success(action === 'keep'
          ? 'Kept.'
          : action === 'hide'
            ? 'Hidden. It won’t show or be used by Guru.'
            : isSharepoint
              ? 'Marked for deletion. See the SharePoint cleanse tab.'
              : 'Removed from the Building Passport.')
      })
      .catch(() => toast.error('Could not apply that action. Please try again.'))
      .finally(() => setBusyId(null))
  }

  const applyBulk = (action) => {
    if (bulkBusy) return
    const chosen = files.filter(f => selection.isSelected(f.id))
    if (!chosen.length) return
    const perFile = action === 'delete' ? chosen.filter(f => f.provenance !== 'sharepoint') : chosen
    const markSp = action === 'delete' ? chosen.filter(f => f.provenance === 'sharepoint') : []
    if (action === 'delete' && !window.confirm(
      markSp.length
        ? (perFile.length
          ? `Remove ${perFile.length} file${perFile.length === 1 ? '' : 's'} and mark ${markSp.length} SharePoint file${markSp.length === 1 ? '' : 's'} for deletion?`
          : `Mark ${markSp.length} SharePoint file${markSp.length === 1 ? '' : 's'} for deletion? They move to the SharePoint cleanse list.`)
        : `Remove ${perFile.length} file${perFile.length === 1 ? '' : 's'} from this Building Passport? They move to the Actioned tab.`
    )) return
    setBulkBusy(true)
    const calls = [
      ...(markSp.length ? [markFilesForDeletion(bpId, markSp.map(f => f.id))] : []),
      ...perFile.map(f => setFileReviewAction(bpId, f.id, action))
    ]
    Promise.allSettled(calls)
      .then(results => {
        const failed = results.filter(r => r.status === 'rejected').length
        const spOk = markSp.length ? results[0].status === 'fulfilled' : true
        const perFileResults = markSp.length ? results.slice(1) : results
        const succeededIds = new Set()
        if (spOk) markSp.forEach(f => succeededIds.add(f.id))
        perFile.forEach((f, i) => { if (perFileResults[i]?.status === 'fulfilled') succeededIds.add(f.id) })
        setFiles(prev => prev.filter(f => !succeededIds.has(f.id)))
        setMeta(m => (m ? { ...m, total: Math.max(0, (m.total || 0) - succeededIds.size) } : m))
        selection.clear()
        loadActioned(1, sortDir)
        if (failed) toast.error(`${failed} file${failed === 1 ? '' : 's'} could not be updated. Please try again.`)
        else toast.success(`${chosen.length} file${chosen.length === 1 ? '' : 's'} ${action === 'keep' ? 'kept' : action === 'hide' ? 'hidden' : 'actioned'}.`)
      })
      .finally(() => setBulkBusy(false))
  }

  useEffect(() => { resetSelection() }, [source, fileType, fileCategory, flaggedOnly, view, resetSelection])

  const edit = (row, action) => {
    if (busyId || row.action === 'delete' || action === row.action) return
    setBusyId(row.fileId)
    setFileReviewAction(bpId, row.fileId, action)
      .then(() => { loadActioned(1, sortDir); toast.success('Updated.') })
      .catch(() => toast.error('Could not update that action. Please try again.'))
      .finally(() => setBusyId(null))
  }

  const counts = meta?.counts || {}
  const actionedTotal = actionedMeta?.total ?? actioned.length
  const visibleIds = files.map(f => f.id)
  const chosenFiles = files.filter(f => selection.isSelected(f.id))
  const chosenAllSharepoint = chosenFiles.length > 0 && chosenFiles.every(f => f.provenance === 'sharepoint')

  return (
    <>
    <div>
      <SubTabs role='tablist'>
        <SubTab role='tab' aria-selected={view === 'review'} $active={view === 'review'} onClick={() => setView('review')}>
          To review{meta?.total != null ? ` · ${fmt(meta.total)}` : ''}
        </SubTab>
        <SubTab role='tab' aria-selected={view === 'actioned'} $active={view === 'actioned'} onClick={() => setView('actioned')}>
          Actioned{actionedTotal ? ` · ${fmt(actionedTotal)}` : ''}
        </SubTab>
      </SubTabs>

      {view === 'review' ? (
        <>
          <Sub data-tour-id='files.review-explainer'>Decide what to do with each file. <strong>Keep</strong> leaves it in place, <strong>Hide</strong> keeps it but hides it from lists and Guru, <strong>Delete</strong> removes it from the Building Passport (and blocks it re-syncing from SharePoint). Actioned files move to the Actioned tab, where you can change your mind.</Sub>

          <Filters role='group' aria-label='Filter by source'>
            {SOURCES.map(s => (
              <Chip key={s.key} $active={source === s.key} aria-pressed={source === s.key} onClick={() => setSource(s.key)}>
                {s.label}{meta ? <Num>{fmt(counts[s.key])}</Num> : null}
              </Chip>
            ))}
          </Filters>

          <Toolbar>
            <SearchBox>
              <SearchRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder='Search files by name' />
            </SearchBox>
            <FilterSelect label='File type' value={fileType} options={typeOptions} onChange={setFileType} />
            <FilterSelect label='Category' value={fileCategory} options={filters.categories} onChange={onSelectCategory} />
            {(fileType || fileCategory) && <ClearLink type='button' onClick={clearTypeFilters}>Clear filters</ClearLink>}
            {(counts.flagged > 0 || flaggedOnly) && (
              <Chip type='button' $active={flaggedOnly} aria-pressed={flaggedOnly} title='Show only files the AI flagged as possibly the wrong type' onClick={() => setFlaggedOnly(v => !v)}>
                <WarningAmberRoundedIcon style={{ fontSize: 14 }} /> Flagged by AI{counts.flagged ? <Num>{fmt(counts.flagged)}</Num> : null}
              </Chip>
            )}
          </Toolbar>

          {loading ? (
            <Skeletons>{Array.from({ length: 6 }).map((_, i) => <SkelRow key={i} />)}</Skeletons>
          ) : files.length === 0 ? (
            <Empty>{query ? 'No files match your search.' : source === 'sharepoint' ? 'No SharePoint files left to review.' : 'Nothing left to review here.'}</Empty>
          ) : (
            <>
              <CountLine>Showing {files.length} of {fmt(meta?.total ?? files.length)}</CountLine>
              {canEdit && <ReviewActionsLegend hasSharepoint={source === 'sharepoint' || files.some(f => f.provenance === 'sharepoint')} />}
              {canEdit && (
                <BulkActionBar count={chosenFiles.length} onClear={selection.clear} busy={bulkBusy} noun='file'>
                  <BulkBtn $tone='keep' disabled={bulkBusy} onClick={() => applyBulk('keep')}>Keep</BulkBtn>
                  <BulkBtn $tone='hide' disabled={bulkBusy} onClick={() => applyBulk('hide')}>Hide</BulkBtn>
                  <BulkBtn $tone={chosenAllSharepoint ? 'mark' : 'delete'} disabled={bulkBusy} onClick={() => applyBulk('delete')}>{chosenAllSharepoint ? 'Mark for deletion' : 'Delete'}</BulkBtn>
                </BulkActionBar>
              )}
              <Table data-tour-id='files.review-list'>
                <THead>
                  <TH $w='34%'>
                    <HeadFile>
                      {canEdit && <Check checked={visibleIds.length > 0 && selection.allSelected(visibleIds)} onChange={() => selection.toggleAll(visibleIds)} title='Select all shown' aria-label='Select all shown files' />}
                      <span>File</span>
                    </HeadFile>
                  </TH>
                  <TH $w='11%'>From</TH>
                  <TH $w='13%'>Uploaded by</TH>
                  <TH $w='14%'>Date added</TH>
                  <TH $w='28%' style={{ textAlign: 'right' }}>Action</TH>
                </THead>
                {files.map((f, idx) => (
                  <TRow key={f.id} $tall>
                    <TD $w='34%'>
                      <FileCell>
                        {canEdit && <Check checked={selection.isSelected(f.id)} onChange={() => selection.toggle(f.id)} onClick={e => e.stopPropagation()} aria-label={`Select ${f.displayName}`} />}
                        <FileMain>
                          <Name as='button' type='button' title='View document insight' onClick={() => onOpenInsight?.({ id: f.id, displayName: f.displayName })}>{f.displayName}</Name>
                          <RowMeta>
                            {f.fileCategory && <MiniChip $kind='cat'>{f.fileCategory}</MiniChip>}
                            {f.fileType ? <MiniChip $kind='type'>{f.fileType}</MiniChip> : <MiniChip $kind='none'>Unclassified</MiniChip>}
                            {f.aiFlag?.suggestedType && (
                              <AiFlagChip title={`Uploaded as ${f.fileType || 'a type'}, but the AI suggests this looks more like ${f.aiFlag.suggestedType}. Please verify the type.`}>
                                <WarningAmberRoundedIcon style={{ fontSize: 11 }} /> AI: {f.aiFlag.suggestedType}?
                              </AiFlagChip>
                            )}
                            <FileRef type='button' title='Copy file reference' onClick={() => copyRef(f.id)}>
                              <ContentCopyRoundedIcon style={{ fontSize: 11 }} /> {shortId(f.id)}
                            </FileRef>
                          </RowMeta>
                        </FileMain>
                      </FileCell>
                    </TD>
                    <TD $w='11%'><ProvenanceChip provenance={f.provenance} /></TD>
                    <TD $w='13%'><Muted>{f.uploader}</Muted></TD>
                    <TD $w='14%'><DateCell><span>{formatDate(f.createdAt)}</span><small>{formatTime(f.createdAt)}</small></DateCell></TD>
                    <TD $w='28%' style={{ textAlign: 'right' }}>
                      <RowActions>
                        <PrevBtn type='button' disabled={previewingId === f.id} onClick={() => openPreview(f)}>
                          <VisibilityOutlinedIcon style={{ fontSize: 15 }} /> {previewingId === f.id ? 'Opening…' : 'Preview'}
                        </PrevBtn>
                        {canEdit ? <ReviewActionSlider deleteLabel={f.provenance === 'sharepoint' ? 'Mark' : 'Delete'} disabled={busyId === f.id} onChange={a => apply(f, a)} tourId={idx === 0} /> : <Muted>-</Muted>}
                      </RowActions>
                    </TD>
                  </TRow>
                ))}
              </Table>
              <InfiniteScrollSentinel
                hasMore={(meta?.currentPage ?? 1) < (meta?.lastPage ?? 1)}
                loading={loadingMore}
                onLoadMore={() => load((meta?.currentPage ?? 1) + 1, query, source, fileType, fileCategory, flaggedOnly)}
              />
            </>
          )}
        </>
      ) : (
        <>
          <Sub>Files you’ve actioned. <strong>Keep</strong> and <strong>Hide</strong> can be changed here; <strong>Delete</strong> is final.</Sub>
          {actionedLoading ? (
            <Skeletons>{Array.from({ length: 5 }).map((_, i) => <SkelRow key={i} />)}</Skeletons>
          ) : actioned.length === 0 ? (
            <Empty>No files actioned yet. Use Keep / Hide / Delete on the To review tab and they’ll appear here.</Empty>
          ) : (
            <>
              <CountLine>Showing {actioned.length} of {fmt(actionedTotal)}</CountLine>
              <Table>
                <THead>
                  <TH $w='28%'>File</TH>
                  <TH $w='13%'>From</TH>
                  <TH $w='13%'>Action</TH>
                  <TH $w='16%'>By</TH>
                  <TH $w='12%' $sortable onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}>
                    Date {sortDir === 'asc' ? '▲' : '▼'}
                  </TH>
                  <TH $w='18%' style={{ textAlign: 'right' }}>Change</TH>
                </THead>
                {actioned.map(row => (
                  <TRow key={row.id} $muted={row.action === 'delete'}>
                    <TD $w='28%'>
                      {row.fileId
                        ? <Name as='button' type='button' title='View document insight' $struck={row.action === 'delete'} onClick={() => onOpenInsight?.({ id: row.fileId, displayName: row.fileName })}>{row.fileName}</Name>
                        : <Muted>{row.fileName}</Muted>}
                    </TD>
                    <TD $w='13%'><ProvenanceChip provenance={row.provenance} /></TD>
                    <TD $w='13%'><ActionTag $a={row.action}>{row.action}</ActionTag></TD>
                    <TD $w='16%'><Muted>{row.actedBy}</Muted></TD>
                    <TD $w='12%'><Muted>{formatDate(row.actedAt)}</Muted></TD>
                    <TD $w='18%' style={{ textAlign: 'right' }}>
                      {!row.fileId
                        ? <Muted>Restricted</Muted>
                        : canEdit && row.action !== 'delete'
                          ? <ReviewActionSlider value={row.action} disabled={busyId === row.fileId} onChange={a => edit(row, a)} />
                          : <Muted>Removed</Muted>}
                    </TD>
                  </TRow>
                ))}
              </Table>
              <InfiniteScrollSentinel
                hasMore={(actionedMeta?.currentPage ?? 1) < (actionedMeta?.lastPage ?? 1)}
                loading={actionedLoadingMore}
                onLoadMore={() => loadActioned((actionedMeta?.currentPage ?? 1) + 1, sortDir)}
              />
            </>
          )}
        </>
      )}
    </div>
    {previewModal}
    </>
  )
}

export default ReviewPanel

const SubTabs = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 14px;
`

const SubTab = styled.button`
  border: none;
  background: transparent;
  padding: 9px 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  color: ${({ $active }) => ($active ? '#1e293b' : '#64748b')};
  border-bottom: 2px solid ${({ $active }) => ($active ? '#3b5ba5' : 'transparent')};
  margin-bottom: -1px;

  &:hover { color: #1e293b; }
`

const Sub = styled.div`
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 12px;
  strong { color: #334155; font-weight: 700; }
`

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ $active }) => ($active ? '#cbd5e1' : '#e2e8f0')};
  background: ${({ $active }) => ($active ? '#eef2f7' : '#fff')};
  color: ${({ $active }) => ($active ? '#1e293b' : '#64748b')};
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover { border-color: #cbd5e1; color: #1e293b; }
`

const Num = styled.span`
  font-size: 11.5px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  padding: 1px 7px;
  border-radius: 999px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
`

const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  flex: 1 1 240px;
  min-width: 200px;
  max-width: 380px;

  input { border: none; outline: none; font-size: 14px; width: 100%; background: transparent; color: #1e293b; }
`

const ClearLink = styled.button`
  border: none; background: transparent; color: #b00020; font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer;
  &:hover { text-decoration: underline; }
`

const CountLine = styled.div`
  font-size: 12.5px;
  color: #64748b;
  margin-bottom: 8px;
`

const Table = styled.div`display: flex; flex-direction: column;`

const THead = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
`

const TH = styled.div`
  width: ${({ $w }) => $w || 'auto'};
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
`

const TRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ $tall }) => ($tall ? '11px 10px' : '9px 10px')};
  border-bottom: 1px solid #f1f5f9;
  opacity: ${({ $muted }) => ($muted ? 0.65 : 1)};
  &:hover { background: #f8fafc; }
`

const TD = styled.div`
  width: ${({ $w }) => $w || 'auto'};
  font-size: 13.5px;
  color: #1e293b;
  padding-right: 8px;
  overflow: hidden;
`

const Name = styled.div`
  font: inherit;
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-weight: 600;
  color: #33becc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: ${({ $struck }) => ($struck ? 'line-through' : 'none')};
  &:hover { text-decoration: ${({ $struck }) => ($struck ? 'line-through' : 'underline')}; }
`

const Muted = styled.span`color: #64748b;`

const RowActions = styled.div`
  display: inline-flex; align-items: center; gap: 8px; justify-content: flex-end; flex-wrap: wrap;
`

const Check = styled.input.attrs({ type: 'checkbox' })`
  width: 16px; height: 16px; margin: 0; cursor: pointer; flex-shrink: 0; accent-color: #4f46e5; vertical-align: middle;
`

const FileCell = styled.div`
  display: flex; align-items: flex-start; gap: 10px; min-width: 0;
`

const HeadFile = styled.div`
  display: flex; align-items: center; gap: 10px;
`

const FileMain = styled.div`
  min-width: 0; flex: 1;
`

const PrevBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
  padding: 5px 11px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer;
  color: #4a4f5e; background: #f1f3f7; border: 1px solid #e6e8ec;
  transition: filter 140ms ease;
  &:hover:not(:disabled) { filter: brightness(0.97); }
  &:disabled { opacity: 0.5; cursor: wait; }
`

const RowMeta = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 5px;
`

const MiniChip = styled.span`
  display: inline-flex; align-items: center; font-size: 10.5px; font-weight: 700; padding: 1px 8px; border-radius: 999px; white-space: nowrap;
  color: ${({ $kind }) => ($kind === 'cat' ? '#3730a3' : $kind === 'none' ? '#92400e' : '#334155')};
  background: ${({ $kind }) => ($kind === 'cat' ? '#eef2ff' : $kind === 'none' ? '#fef3c7' : '#f1f5f9')};
`
const AiFlagChip = styled.span`
  display: inline-flex; align-items: center; gap: 3px; font-size: 10.5px; font-weight: 800; padding: 1px 8px; border-radius: 999px; white-space: nowrap;
  color: #92400e; background: #fef3c7; border: 1px solid #fcd34d;
`

const FileRef = styled.button`
  display: inline-flex; align-items: center; gap: 3px; border: none; background: transparent; cursor: pointer;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 10.5px; color: #94a3b8; padding: 0;
  &:hover { color: #475569; }
`

const DateCell = styled.div`
  display: flex; flex-direction: column; line-height: 1.25;
  span { color: #334155; font-size: 13px; }
  small { color: #94a3b8; font-size: 11.5px; font-variant-numeric: tabular-nums; }
`

const ACTION_TONES = {
  keep: { bg: '#dcfce7', fg: '#15803d' },
  hide: { bg: '#fef3c7', fg: '#b45309' },
  delete: { bg: '#fdecea', fg: '#b00020' }
}

const ActionTag = styled.span`
  display: inline-flex;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  background: ${({ $a }) => (ACTION_TONES[$a] || ACTION_TONES.keep).bg};
  color: ${({ $a }) => (ACTION_TONES[$a] || ACTION_TONES.keep).fg};
`

const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-size: 13.5px;
  color: #64748b;
`

const Skeletons = styled.div`display: flex; flex-direction: column; gap: 8px;`

const SkelRow = styled.div`
  height: 42px;
  border-radius: 8px;
  background: #f1f5f9;
  animation: pulse 1.4s ease-in-out infinite;
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
`
